'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { NO_TRACK_KEY, SESSION_KEY, sendPageView, trackEvent } from '@/lib/track-client';

// 관리자 "접속 통계"·"광고 성과"용 기록. 공개 페이지가 열리거나 페이지가 바뀔 때마다 접속 기록을 한 번 보내고,
// 페이지에 머문 시간·스크롤, 전화·카톡 누름을 함께 남긴다.
// 관리자 화면을 연 적이 있는 브라우저는 통계에서 빠진다 (관리자 본인 방문이 섞이지 않게).
export default function PageViewTracker() {
  const pathname = usePathname();

  // 접속 기록 + 머문 시간·스크롤 (화면이 보이는 동안만 시간을 잰다)
  useEffect(() => {
    if (!pathname) return;
    try {
      if (pathname.startsWith('/admin')) {
        localStorage.setItem(NO_TRACK_KEY, '1');
        return;
      }
      if (pathname.startsWith('/embed')) return;
      if (localStorage.getItem(NO_TRACK_KEY)) return;
      sendPageView(pathname, !sessionStorage.getItem(SESSION_KEY));
    } catch {
      // 저장소가 막힌 브라우저(사생활 보호 모드 등)는 기록하지 않는다
      return;
    }

    let shownAt = document.visibilityState === 'visible' ? Date.now() : 0;
    let unsentMs = 0;
    let maxScroll = 0;
    const measureScroll = () => {
      const h = document.documentElement.scrollHeight;
      if (h > 0) maxScroll = Math.max(maxScroll, Math.min(100, Math.round(((window.scrollY + window.innerHeight) / h) * 100)));
    };
    const flush = () => {
      if (shownAt) {
        unsentMs += Date.now() - shownAt;
        shownAt = 0;
      }
      measureScroll();
      const sec = Math.round(unsentMs / 1000);
      if (sec < 1) return;
      unsentMs -= sec * 1000;
      trackEvent('engage', Math.min(sec, 3600), maxScroll);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
      else shownAt = Date.now();
    };
    measureScroll();
    window.addEventListener('scroll', measureScroll, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flush);
    return () => {
      flush();
      window.removeEventListener('scroll', measureScroll);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
    };
  }, [pathname]);

  // 전화·카톡 링크 누름 (사이트 어디서든)
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a) return;
      const href = a.getAttribute('href') ?? '';
      if (href.startsWith('tel:')) trackEvent('call_click');
      else if (/pf\.kakao\.com|open\.kakao\.com/.test(href)) trackEvent('kakao_click');
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  return null;
}
